import React, { useState, useEffect } from "react";
import {
  Layout, Select, Switch, Space, Row, Col, Breadcrumb, message, Typography,
  Tag, Descriptions, Input, Button, Modal, Form, Popconfirm, Divider,
  Progress, Tooltip, Badge
} from "antd";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import {
  LockOutlined, SearchOutlined, ClearOutlined, HomeOutlined, PlusOutlined,
  EditOutlined, StopOutlined, CheckCircleOutlined, SafetyCertificateOutlined,
  UnlockOutlined, GlobalOutlined, ShoppingOutlined, SnippetsOutlined,
  SlidersOutlined, SettingOutlined, UpOutlined, DownOutlined,
  AccountBookOutlined, BarChartOutlined, TrophyOutlined, ShoppingCartOutlined,
  ExperimentOutlined
} from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import { REPORT_PERMISSION_KEYS } from "../../../utils/permissionHelper";
import "../../../styles/fluidDesign.css";

const { Content } = Layout;
const { Text, Title } = Typography;

const PERMISSION_GROUPS = [
  {
    key: "tasks",
    label: "Tasks",
    icon: <SnippetsOutlined />,
    color: "#3b82f6",
    bgColor: "rgba(59,130,246,0.07)",
    borderColor: "rgba(59,130,246,0.2)",
    prefixes: ["tasks"],
  },
  {
    key: "farmSetUp",
    label: "Farm Setup",
    icon: <GlobalOutlined />,
    color: "#45a186",
    bgColor: "rgba(69,161,134,0.07)",
    borderColor: "rgba(69,161,134,0.2)",
    prefixes: ["farmSetUp"],
  },
  {
    key: "inventory",
    label: "Inventory",
    icon: <ShoppingOutlined />,
    color: "#f59e0b",
    bgColor: "rgba(245,158,11,0.07)",
    borderColor: "rgba(245,158,11,0.2)",
    // The sidebar no longer splits Inventory into submenus (Goods Received /
    // Material Request / Material Issue Note), so every inventory-related
    // permission lives under this single card.
    prefixes: ["pr", "grn", "material_request", "material_issue_note"],
  },
  {
    key: "maintenance",
    label: "Maintenance",
    icon: <SettingOutlined />,
    color: "#8b5cf6",
    bgColor: "rgba(139,92,246,0.07)",
    borderColor: "rgba(139,92,246,0.2)",
    prefixes: ["maintenance"],
  },
  {
    key: "expenses",
    label: "Expenses",
    icon: <AccountBookOutlined />,
    color: "#0ea5e9",
    bgColor: "rgba(14,165,233,0.07)",
    borderColor: "rgba(14,165,233,0.2)",
    prefixes: ["expenses"],
  },
  {
    key: "reports",
    label: "Reports",
    icon: <BarChartOutlined />,
    color: "#f97316",
    bgColor: "rgba(249,115,22,0.07)",
    borderColor: "rgba(249,115,22,0.2)",
    prefixes: ["reports"],
  },
  {
    key: "harvesting",
    label: "Harvesting",
    icon: <TrophyOutlined />,
    color: "#d97706",
    bgColor: "rgba(217,119,6,0.07)",
    borderColor: "rgba(217,119,6,0.2)",
    prefixes: ["harvesting"],
  },
  {
    key: "production",
    label: "Production",
    icon: <ExperimentOutlined />,
    color: "#7c3aed",
    bgColor: "rgba(124,58,237,0.07)",
    borderColor: "rgba(124,58,237,0.2)",
    prefixes: ["prod"],
  },
  {
    key: "sales_products",
    label: "Sales Products",
    icon: <ShoppingCartOutlined />,
    color: "#10b981",
    bgColor: "rgba(16,185,129,0.07)",
    borderColor: "rgba(16,185,129,0.2)",
    prefixes: ["sales_products"],
  },
  {
    key: "customers",
    label: "Customers",
    icon: <SafetyCertificateOutlined />,
    color: "#0891b2",
    bgColor: "rgba(8,145,178,0.07)",
    borderColor: "rgba(8,145,178,0.2)",
    prefixes: ["customers"],
  },
  {
    key: "settings",
    label: "Settings",
    icon: <SlidersOutlined />,
    color: "#ef4444",
    bgColor: "rgba(239,68,68,0.07)",
    borderColor: "rgba(239,68,68,0.2)",
    prefixes: ["settings"],
  },
];

/**
 * Permissions belonging to the Material Request / Inventory Issuing
 * modules. These are ensured to exist for every role opened in this
 * page (mirrors the settings_suppliers self-healing pattern) so the
 * sub-module sections inside the Inventory card always render.
 */
const MODULE_PERMISSIONS = [
  "material_request",
  "material_request_create",
  "material_request_list",
  "material_request_approvals",
  "material_request_accept",
  "material_request_view_approvals",
  "material_issue_note",
  "material_issue_note_prepare",
  "material_issue_note_list",
  "material_issue_note_ready",
  "material_issue_note_issue",
  "customers",
  "customers_list",
  "customers_manage",
  "harvesting",
  "harvesting_management",
  "harvesting_management_harvest",
  "harvesting_harvest_batches_view",
  "harvesting_harvest_batches_create",
  "harvesting_harvest_batches_edit",
  "harvesting_harvest_batches_delete",
  "harvesting_harvest_batches_view_details",
  "production",
  "prod_batches",
  "prod_batches_view",
  "prod_batches_create",
  "prod_batches_edit",
  "prod_batches_delete",
  "prod_batches_details",
  "prod_batches_start",
  "prod_batches_finish",
  "prod_batches_export",
  "prod_batches_label",
  "prod_batches_assign_harvest",
  "prod_process",
  "prod_process_view",
  "prod_process_input",
  "prod_process_approve",
  "prod_process_qc",
  "prod_process_export",
  "prod_logs",
  "prod_logs_view",
  "prod_logs_export",
  "sales_products",
  "sales_products_list",
  "sales_products_manage",
  "settings_salesProductCategories",
];

/**
 * Permissions ensured for every role opened in this page. Report permissions
 * are included so the Reports card always lists a toggle for every report
 * (each report page is guarded by its own reports_* permission).
 */
const AUTO_SEED_PERMISSIONS = [...MODULE_PERMISSIONS, ...REPORT_PERMISSION_KEYS];

/**
 * Legacy hyphenated/plural permission names from earlier seeds. They are
 * hidden from the UI (and removed by DB_BACKUP/add_material_request_permissions.sql).
 */
const isLegacyModulePermission = (name) =>
  name.startsWith("material-requests") ||
  name.startsWith("material-issue-note") ||
  name.startsWith("material_requests");

/**
 * Sub-module master keys that live inside the Inventory card. They no longer
 * gate a sidebar submenu (those were removed) but are kept as toggleable rows
 * that cascade to their own action permissions.
 */
const SUB_MODULE_KEYS = ["grn", "material_request", "material_issue_note", "prod_batches", "prod_process", "prod_logs"];

/**
 * Human-friendly labels for permission keys that do not format well with the
 * generic algorithm (single-word module rows and the Material Request /
 * Material Issue Note action permissions). Mirrors the sidebar labels.
 */
const PERM_LABELS = {
  grn: "GRN Module",
  grn_create: "Create GRN",
  grn_list: "GRN List",
  grn_approvals: "GRN Approvals",
  material_request: "Material Request Module",
  material_request_create: "Create Material Request",
  material_request_list: "Material Requests List",
  material_request_approvals: "Material Request Approvals",
  material_request_accept: "Accept Material Requests",
  material_request_view_approvals: "View Approved Material Request Approvals",
  material_issue_note: "Material Issue Note Module",
  material_issue_note_prepare: "Prepare Issue Note",
  material_issue_note_list: "Material Issue Notes List",
  material_issue_note_ready: "Ready for Issue",
  material_issue_note_issue: "Issue Items",
  expenses_list: "Expenses List",
  expenses_approve: "Approve Expenses",
  expenses_payment_create: "Create Payment",
  expenses_payment_settle: "Payment Settle",
  customers: "Customers Module",
  customers_list: "Customer List",
  customers_manage: "Manage Customers",
  harvesting: "Harvesting Module",
  harvesting_management: "Harvest Management",
  harvesting_management_harvest: "Submit Harvest",
  harvesting_harvest_batches_view: "View Harvest Batches",
  harvesting_harvest_batches_create: "Create Harvest Batches",
  harvesting_harvest_batches_edit: "Edit Harvest Batches",
  harvesting_harvest_batches_delete: "Delete Harvest Batches",
  harvesting_harvest_batches_view_details: "View Full Details",
  production: "Production Module",
  prod_batches: "Production Batches",
  prod_batches_view: "View Production Batches",
  prod_batches_create: "Create Production Batch",
  prod_batches_edit: "Edit Production Batch",
  prod_batches_delete: "Delete Production Batch",
  prod_batches_details: "View Production Batch Details",
  prod_batches_start: "Start Production",
  prod_batches_finish: "Finish Production",
  prod_batches_export: "Export Production Batches",
  prod_batches_label: "Print Production Batch Labels",
  prod_batches_assign_harvest: "Assign Harvest Batch to Production Batch",
  prod_process: "Production Process",
  prod_process_view: "View Production Process",
  prod_process_input: "Add Production Input",
  prod_process_approve: "Approve Production Output",
  prod_process_qc: "Perform Quality Control",
  prod_process_export: "Export Production Process",
  prod_logs: "Production Logs",
  prod_logs_view: "View Production Logs",
  prod_logs_export: "Export Production Logs",
  sales_products: "Sales Products Module",
  sales_products_list: "Sales Products List",
  sales_products_manage: "Manage Sales Products",
  settings_salesProductCategories: "Sales Product Categories",
  // Reports: one permission per report (mirrors the All Reports page).
  reports_inventory: "Inventory Report",
  reports_inventory_items: "Inventory Items Report",
  reports_inventory_stockadjustments: "Stock Adjustments Report",
  reports_inventory_purchase_requests: "Purchase Requests Report",
  reports_inventory_grn: "GRN Report",
  reports_inventory_material_requests: "Material Requests Report",
  reports_inventory_issue_notes: "Issue Notes Report",
  reports_inventory_stock_valuation: "Stock Valuation Report",
  reports_inventory_low_stock: "Low Stock Report",
  reports_maintenance: "Maintenance Tasks Report",
  reports_expenses: "Expenses Report",
  reports_expenses_cash_flow: "Cash Flow Report",
  reports_expenses_payments: "Payments Report",
  reports_farmSetup: "Farm Setup Reports",
  reports_farm_plants: "Plants Register Report",
};

const formatPermissionLabel = (name) => {
  // Explicit human-friendly labels (sub-module masters + Material Request /
  // Material Issue Note actions) take precedence.
  if (PERM_LABELS[name]) return PERM_LABELS[name];

  // A module key itself is the master "Module Access" permission
  if (PERMISSION_GROUPS.some((g) => g.key === name)) return "Module Access";

  const parts = name.split("_");
  if (parts.length === 1) return "Module Access";
  
  // Special handling: put meaningful short prefixes (like 'pr') at the end
  // e.g. pr_create → "Create PR"
  const suffixPrefixes = ["pr", "grn"];
  if (suffixPrefixes.includes(parts[0]) && parts.length > 1) {
    const rest = parts.slice(1).join(" ");
    const formatted = rest
      .replace(/([A-Z])/g, " $1")
      .replace(/^\s/, "")
      .replace(/\b\w/g, (l) => l.toUpperCase());
    return `${formatted} ${parts[0].toUpperCase()}`;
  }
  
  return parts
    .slice(1)
    .join(" ")
    .replace(/([A-Z])/g, " $1")
    .replace(/^\s/, "")
    .replace(/\b\w/g, (l) => l.toUpperCase());
};


// A parent permission is a registered group key (e.g. 'inventory', 'settings').
// Module rows for GRN / Material Request / Material Issue Note are treated as
// sub-module children inside the Inventory card (their sidebar submenus no
// longer exist) and cascade to their own action permissions when toggled.
const isParentPermission = (name) =>
  PERMISSION_GROUPS.some((g) => g.key === name);

const normalizePermission = (permission) => ({
  ...permission,
  per_name: String(permission.per_name || "").trim(),
});

const hasPermission = (permissions, permissionName) =>
  permissions.some((permission) => permission.per_name === permissionName);

const getDefaultPermissionStatus = (roleData) => {
  if (!roleData) return 0;
  return String(roleData.role_id) === "1" ||
    String(roleData.role_name || "").toLowerCase().includes("admin")
    ? 1
    : 0;
};


const isPermissionInGroup = (group, permissionName) =>
  permissionName === group.key ||
  permissionName.startsWith(group.key + "_") ||
  (group.prefixes &&
    group.prefixes.some(
      (p) =>
        p !== group.key &&
        (permissionName === p || permissionName.startsWith(p + "_"))
    ));

const groupPermissions = (permissions) => {
  const grouped = {};
  PERMISSION_GROUPS.forEach((g) => {
    grouped[g.key] = { ...g, items: [] };
  });
  const ungrouped = [];

  permissions.forEach((perm) => {
    const permissionName = String(perm.per_name || "").trim();
    let placed = false;
    for (const group of PERMISSION_GROUPS) {
      if (isPermissionInGroup(group, permissionName)) {
        grouped[group.key].items.push({ ...perm, per_name: permissionName });
        placed = true;
        break;
      }
    }
    if (!placed) ungrouped.push({ ...perm, per_name: permissionName });
  });

  return { grouped, ungrouped };
};

/**
 * Optional sub-module sections rendered inside a group card's expanded child
 * list. The Inventory card now holds every inventory-related permission (after
 * the sidebar restructure), so its children are visually grouped by sub-module.
 * Unmatched children fall back to the group's own label.
 */
const SECTION_MATCHERS = {
  // Reports: children are grouped by report category, mirroring the
  // All Reports page (Inventory / Maintenance / Expenses / Farm Setup).
  reports: [
    {
      match: (name) =>
        name === "reports_inventory" ||
        name === "reports_inventory_items" ||
        name.startsWith("reports_inventory_"),
      label: "Inventory Reports",
    },
    {
      match: (name) =>
        name === "reports_maintenance" || name.startsWith("reports_maintenance_"),
      label: "Maintenance Reports",
    },
    {
      match: (name) =>
        name === "reports_expenses" || name.startsWith("reports_expenses_"),
      label: "Expenses Reports",
    },
    {
      match: (name) =>
        name === "reports_farmSetup" ||
        name === "reports_farm_plants" ||
        name.startsWith("reports_farm_"),
      label: "Farm Setup Reports",
    },
  ],
  // Mirrors the sidebar: Harvesting (batches + collection).
  harvesting: [
    {
      match: (name) =>
        name === "harvesting" ||
        name === "harvesting_management" ||
        name === "harvesting_management_harvest" ||
        name.startsWith("harvesting_harvest_batches"),
      label: "Harvesting",
    },
  ],
  // Production is its own top-level card. Children are grouped by the sidebar
  // subsections: Production Batches, Production Process then Production Logs.
  production: [
    {
      match: (name) => name === "prod_batches" || name.startsWith("prod_batches_"),
      label: "Production Batches",
    },
    {
      match: (name) => name === "prod_process" || name.startsWith("prod_process_"),
      label: "Production Process",
    },
    {
      match: (name) => name === "prod_logs" || name.startsWith("prod_logs_"),
      label: "Production Logs",
    },
  ],
  // Ordered to mirror the sidebar: core inventory first, then Purchase
  // Requests, Goods Received (GRN), Material Request and Material Issue Note.
  inventory: [
    {
      match: (name) =>
        name === "inventory_purchase_requests" ||
        name.startsWith("inventory_purchase_requests"),
      label: "Purchase Requests",
    },
    {
      match: (name) => name === "pr" || name.startsWith("pr_"),
      label: "Purchase Requests",
    },
    {
      match: (name) => name === "grn" || name.startsWith("grn_"),
      label: "Goods Received (GRN)",
    },
    {
      match: (name) => name === "material_request" || name.startsWith("material_request_"),
      label: "Material Request",
    },
    {
      match: (name) => name === "material_issue_note" || name.startsWith("material_issue_note_"),
      label: "Material Issue Note",
    },
  ],
};

const getChildSections = (group, childItems) => {
  const matchers = SECTION_MATCHERS[group.key];
  const sections = [];

  if (matchers) {
    // Collect sections in matcher order so the card mirrors the sidebar,
    // regardless of the API's alphabetical ordering.
    const placed = new Set();
    matchers.forEach((matcher) => {
      const items = childItems.filter((item) => matcher.match(item.per_name));
      if (items.length > 0) {
        sections.push({ label: matcher.label, items });
        items.forEach((item) => placed.add(item.per_name));
      }
    });

    // Unmatched children (e.g. inventory_categories / items / stock
    // adjustments) form the card's own section, shown first like the sidebar.
    const fallbackItems = childItems.filter((item) => !placed.has(item.per_name));
    if (fallbackItems.length > 0) {
      sections.unshift({ label: group.label, items: fallbackItems });
    }
  } else if (childItems.length > 0) {
    sections.push({ label: group.label, items: childItems });
  }

  return sections;
};

const ManageRoles = () => {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedRoleData, setSelectedRoleData] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [filteredPermissions, setFilteredPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [togglingAll, setTogglingAll] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});

  // Role Modal States
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const UPDATED_BY = localStorage.getItem("USERID");

  const syncCurrentUserPermissions = (roleId, rolePermissions) => {
    try {
      const user = JSON.parse(localStorage.getItem("USER") || "{}");
      if (String(user.roleId) !== String(roleId)) {
        return;
      }

      rolePermissions.forEach((perm) => {
        const permissionName = String(perm.per_name || "").trim();
        if (!permissionName) {
          return;
        }

        const isDisabled = Number(perm.per_status) !== 1;
        localStorage.setItem(`USERPERMISSION_${permissionName}`, isDisabled.toString());
      });

      window.dispatchEvent(new Event("permissionsUpdated"));
    } catch (error) {
      console.error("Failed to sync local permission state:", error);
    }
  };

  useEffect(() => {
    fetchRoles();

  }, []);

  useEffect(() => {
    if (searchText) {
      const filtered = permissions.filter((p) =>
        p.per_name.toLowerCase().includes(searchText.toLowerCase()) ||
        formatPermissionLabel(p.per_name).toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredPermissions(filtered);

      const newExpanded = {};
      PERMISSION_GROUPS.forEach((g) => {
        // Auto-expand any group whose members match the search. Uses the full
        // group-membership logic so searching for e.g. "GRN" expands the
        // Inventory card (which now holds all inventory-related permissions).
        const matches = filtered.some((p) => isPermissionInGroup(g, p.per_name));
        if (matches) {
          newExpanded[g.key] = true;
        }
      });
      setExpandedGroups(newExpanded);
    } else {
      setFilteredPermissions(permissions);
    }
  }, [permissions, searchText]);

  const toggleGroupExpand = (groupKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const fetchRoles = async (selectId = null) => {
    try {
      setLoading(true);
      const response = await api.get("/settings/security/role/listall.php");
      const rolesData = response.data.dataset || [];
      setRoles(rolesData);

      if (selectId) {
        const found = rolesData.find((r) => r.role_id === selectId);
        if (found) {
          handleRoleChange(selectId, rolesData);
          return;
        }
      }
      if (rolesData.length > 0 && !selectedRole) {
        const firstRole = rolesData[0];
        handleRoleChange(firstRole.role_id, rolesData);
      }
    } catch (error) {
      message.error("Failed to fetch roles.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch permissions for a specific role
  const fetchPermissions = async (roleId) => {
    try {
      setLoading(true);
      const response = await api.get(
        `/settings/security/permission/listall.php?role_id=${roleId}`
      );
      let permissionsData = (response.data.dataset || [])
        .map(normalizePermission)
        .filter(
          (permission) => permission.per_name && !isLegacyModulePermission(permission.per_name)
        );

      // Self-heal: ensure the Material Request / Inventory Issuing module
      // permissions exist for this role so the module cards and their
      // master switches render (mirrors the settings_suppliers pattern below).
      const missingModulePermissions = AUTO_SEED_PERMISSIONS.filter(
        (permName) => !hasPermission(permissionsData, permName)
      );
      if (missingModulePermissions.length > 0) {
        const roleData = roles.find((role) => String(role.role_id) === String(roleId)) || selectedRoleData;
        for (const permName of missingModulePermissions) {
          try {
            const createResponse = await api.post("/settings/security/permission/create.php", {
              role_id: roleId,
              per_name: permName,
              per_status: getDefaultPermissionStatus(roleData),
            });
            if (createResponse.data.status === "success" && createResponse.data.permission) {
              permissionsData = [
                ...permissionsData,
                normalizePermission(createResponse.data.permission),
              ];
            }
          } catch (error) {
            // Ignore individual failures; they can also be seeded via
            // DB_BACKUP/add_material_request_permissions.sql
          }
        }
      }

      if (!hasPermission(permissionsData, "settings_suppliers")) {
        const roleData = roles.find((role) => String(role.role_id) === String(roleId)) || selectedRoleData;
        const createResponse = await api.post("/settings/security/permission/create.php", {
          role_id: roleId,
          per_name: "settings_suppliers",
          per_status: getDefaultPermissionStatus(roleData),
        });

        if (createResponse.data.status === "success" && createResponse.data.permission) {
          permissionsData = [
            ...permissionsData,
            normalizePermission(createResponse.data.permission),
          ];
        } else {
          const retryResponse = await api.get(
            `/settings/security/permission/listall.php?role_id=${roleId}`
          );
          permissionsData = (retryResponse.data.dataset || [])
            .map(normalizePermission)
            .filter((permission) => permission.per_name);
        }
      }

      setPermissions(permissionsData);
      syncCurrentUserPermissions(roleId, permissionsData);
    } catch (error) {
      message.error("Failed to fetch permissions.");
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (roleId, currentRoles = roles) => {
    setSelectedRole(roleId);
    setSearchText("");
    setExpandedGroups({});
    const roleData = currentRoles.find((r) => r.role_id === roleId);
    setSelectedRoleData(roleData);
    if (roleId) {
      fetchPermissions(roleId);
    } else {
      setPermissions([]);
      setFilteredPermissions([]);
      setSelectedRoleData(null);
    }
  };

  const handleShowCreateModal = () => {
    setModalMode("create");
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleShowEditModal = () => {
    setModalMode("edit");
    form.setFieldsValue({ role_name: selectedRoleData.role_name });
    setIsModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      let response;
      if (modalMode === "create") {
        response = await api.post("/settings/security/role/create.php", {
          role_name: values.role_name,
          updatedby: UPDATED_BY,
        });
      } else {
        response = await api.post("/settings/security/role/update.php", {
          role_id: selectedRole,
          role_name: values.role_name,
          updatedby: UPDATED_BY,
        });
      }
      if (response.data.status === "success") {
        message.success(response.data.message);
        setIsModalVisible(false);
        fetchRoles(
          modalMode === "create" ? response.data.role_id : selectedRole
        );
      } else {
        message.error(response.data.message);
      }
    } catch (error) {
      message.error("Operation failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisableRole = async () => {
    try {
      setLoading(true);
      const response = await api.post("/settings/security/role/delete.php", {
        role_id: selectedRole,
        updatedby: UPDATED_BY,
      });
      if (response.data.status === "success") {
        message.success(response.data.message);
        setSelectedRole(null);
        setSelectedRoleData(null);
        fetchRoles();
      } else {
        message.error(response.data.message);
      }
    } catch (error) {
      message.error("Failed to disable role.");
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionToggle = async (record, checked) => {
    try {
      const newStatus = checked ? 1 : 0;

      // Toggling a module (parent) permission cascades to every sub-permission
      // in its group (e.g. disabling 'inventory' disables inventory_*, pr_*,
      // grn_*, material_request_* and material_issue_note_*).
      let affected = [record];
      if (isParentPermission(record.per_name)) {
        const group = PERMISSION_GROUPS.find((g) => g.key === record.per_name);
        if (group) {
          const children = permissions.filter(
            (p) =>
              p.roleper_id !== record.roleper_id &&
              isPermissionInGroup(group, p.per_name)
          );
          affected = [...affected, ...children];
        }
      } else if (SUB_MODULE_KEYS.includes(record.per_name)) {
        // Sub-module masters (GRN / Material Request / Material Issue Note)
        // cascade to their own action permissions only.
        const children = permissions.filter(
          (p) =>
            p.roleper_id !== record.roleper_id &&
            p.per_name.startsWith(record.per_name + "_")
        );
        affected = [...affected, ...children];
      }

      const payload = {
        roleper_id: record.roleper_id,
        per_status: newStatus,
        updatedby: UPDATED_BY,
      };
      if (affected.length > 1) {
        payload.cascade_ids = affected.slice(1).map((p) => p.roleper_id);
      }

      const response = await api.post("/settings/security/permission/update.php", payload);
      if (response.data.status === "success") {
        const affectedIds = new Set(affected.map((p) => p.roleper_id));
        setPermissions((prev) =>
          prev.map((p) =>
            affectedIds.has(p.roleper_id)
              ? { ...p, per_status: newStatus }
              : p
          )
        );
        syncCurrentUserPermissions(
          selectedRole,
          affected.map((p) => ({ ...p, per_status: newStatus }))
        );
      } else {
        message.error(response.data.message);
      }
    } catch (error) {
      message.error("Failed to update permission.");
    }
  };

  const handleToggleAll = async (status) => {
    try {
      setTogglingAll(true);
      const response = await api.post("/settings/security/permission/toggleall.php", {
        role_id: selectedRole,
        per_status: status,
        updatedby: UPDATED_BY,
      });
      if (response.data.status === "success") {
        message.success(response.data.message);
        syncCurrentUserPermissions(selectedRole, permissions.map((perm) => ({ ...perm, per_status: status })));
        fetchPermissions(selectedRole);
      } else {
        message.error(response.data.message);
      }
    } catch (error) {
      message.error("Failed to update all permissions.");
    } finally {
      setTogglingAll(false);
    }
  };

  const enabledCount = filteredPermissions.filter(
    (p) => p.per_status === 1 || p.per_status === "1"
  ).length;
  const totalCount = filteredPermissions.length;
  const enabledPct = totalCount > 0 ? Math.round((enabledCount / totalCount) * 100) : 0;

  const { grouped, ungrouped } = groupPermissions(filteredPermissions);

  return (
    <Layout className="fluid-page">
      <Headerdiv />

      {/* Page Header */}
      <div className="fluid-page-header">
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <Row align="bottom" justify="space-between">
          <Col>
                          <Breadcrumb
                  className="fluid-breadcrumb"
                  items={[
                      { href: '/dash', title: <HomeOutlined /> },
                      { title: 'Settings' },
                      { title: 'Role Permissions' },
                  ]}
              />
            <h1 className="fluid-page-title">
              <LockOutlined /> Role Permissions
            </h1>
          </Col>
          <Col xs={0} md={12} style={{ textAlign: "right" }}>
            <p className="fluid-page-subtitle">
              Manage access permissions for each system role
            </p>
          </Col>
        </Row>
      </div>

      <Content className="fluid-content">

        {/* ── Role Selection Card ── */}
        <div className="fluid-card" style={{ marginBottom: 16 }}>
          <div className="fluid-card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 className="fluid-card-title">
              <SafetyCertificateOutlined style={{ marginRight: 8, color: "var(--fluid-primary)" }} />
              Select Role
            </h3>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleShowCreateModal}
              className="fluid-button-primary"
            >
              Add New Role
            </Button>
          </div>

          <Row gutter={[16, 12]} align="middle">
            <Col xs={24} md={8}>
              <Select allowClear
                placeholder="Select a role to manage"
                style={{ width: "100%" }}
                value={selectedRole}
                onChange={(val) => handleRoleChange(val)}
                loading={loading}
                size="large"
              >
                {roles.map((role) => (
                  <Select.Option key={role.role_id} value={role.role_id}>
                    <Space>
                      <SafetyCertificateOutlined style={{ color: "var(--fluid-primary)" }} />
                      {role.role_name}
                    </Space>
                  </Select.Option>
                ))}
              </Select>
            </Col>

            {selectedRoleData && (
              <Col xs={24} md={16}>
                <Space split={<Divider type="vertical" />} wrap>
                  <Button icon={<EditOutlined />} onClick={handleShowEditModal}>
                    Rename Role
                  </Button>
                  <Popconfirm
                    title="Disable Role"
                    description="This role will no longer be assignable to users."
                    onConfirm={handleDisableRole}
                    okText="Yes, Disable"
                    cancelText="No"
                    okButtonProps={{ danger: true }}
                  >
                    <Button
                      danger
                      icon={<StopOutlined />}
                      disabled={String(selectedRoleData.role_id) === "1"}
                    >
                      Disable Role
                    </Button>
                  </Popconfirm>
                </Space>
              </Col>
            )}
          </Row>

          {selectedRoleData && (
            <Descriptions
              size="small"
              style={{ marginTop: 16 }}
              bordered
              column={{ xs: 1, sm: 2, md: 4 }}
            >
              <Descriptions.Item label="Role ID">
                <Tag color="blue">#{selectedRoleData.role_id}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Role Name">
                <Text strong>{selectedRoleData.role_name}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color="green" icon={<CheckCircleOutlined />}>Active</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Permissions">
                <Text strong style={{ color: "var(--fluid-primary)" }}>
                  {enabledCount} / {totalCount} enabled
                </Text>
              </Descriptions.Item>
            </Descriptions>
          )}
        </div>

        {/* ── Permissions Section ── */}
        {selectedRole && (
          <>
            {/* Stats + Controls bar */}
            <div className="fluid-card" style={{ marginBottom: 16 }}>
              <Row gutter={[16, 12]} align="middle">
                {/* Search */}
                <Col xs={24} sm={12} md={8}>
                  <Input
                    placeholder="Search permissions..."
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    allowClear
                    className="fluid-input"
                    size="middle"
                  />
                </Col>

                {/* Progress */}
                <Col xs={24} sm={12} md={8}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <Text type="secondary" style={{ whiteSpace: "nowrap", fontSize: 13 }}>
                      {enabledCount} / {totalCount} enabled
                    </Text>
                    <Progress
                      percent={enabledPct}
                      size="small"
                      strokeColor="var(--fluid-primary)"
                      style={{ flex: 1, margin: 0 }}
                      format={(pct) => `${pct}%`}
                    />
                  </div>
                </Col>

                {/* Bulk actions */}
                <Col xs={24} md={8} style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Space>
                    <Popconfirm
                      title="Enable All Permissions"
                      description="Enable ALL permissions for this role?"
                      onConfirm={() => handleToggleAll(1)}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button
                        size="small"
                        type="primary"
                        ghost
                        icon={<UnlockOutlined />}
                        loading={togglingAll}
                      >
                        Enable All
                      </Button>
                    </Popconfirm>
                    <Popconfirm
                      title="Disable All Permissions"
                      description="Disable ALL permissions for this role?"
                      onConfirm={() => handleToggleAll(0)}
                      okText="Yes"
                      cancelText="No"
                      okButtonProps={{ danger: true }}
                    >
                      <Button
                        size="small"
                        danger
                        ghost
                        icon={<LockOutlined />}
                        loading={togglingAll}
                      >
                        Disable All
                      </Button>
                    </Popconfirm>
                  </Space>
                </Col>
              </Row>
            </div>

            {/* ── Permission Groups ── */}
            <Row gutter={[16, 16]}>
              {PERMISSION_GROUPS.map((group) => {
                const groupData = grouped[group.key];
                if (!groupData || groupData.items.length === 0) return null;

                const groupEnabled = groupData.items.filter(
                  (p) => p.per_status === 1 || p.per_status === "1"
                ).length;
                const groupTotal = groupData.items.length;

                const parentItem =
                  groupData.items.find((p) => p.per_name === group.key) ||
                  groupData.items.find((p) => isParentPermission(p.per_name));
                const childItems = groupData.items.filter((p) => !isParentPermission(p.per_name));
                const isExpanded = !!expandedGroups[group.key];

                return (
                  <Col xs={24} md={12} xl={8} key={group.key}>
                    <div
                      className="fluid-card"
                      style={{
                        border: `1.5px solid ${group.borderColor}`,
                        background: group.bgColor,
                        marginBottom: 0,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 12,
                          paddingBottom: 10,
                          borderBottom: `1px solid ${group.borderColor}`,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 8,
                              background: group.color,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "white",
                              fontSize: 15,
                            }}
                          >
                            {group.icon}
                          </div>
                          <div>
                            <Text strong style={{ color: group.color, fontSize: 14 }}>
                              {group.label}
                            </Text>
                            <br />
                            <Text
                              type="secondary"
                              style={{ fontSize: 11 }}
                            >
                              {groupEnabled}/{groupTotal} enabled
                            </Text>
                          </div>
                        </div>
                        <Badge
                          count={groupEnabled}
                          showZero
                          style={{
                            backgroundColor:
                              groupEnabled === groupTotal
                                ? "#52c41a"
                                : groupEnabled === 0
                                  ? "#ff4d4f"
                                  : group.color,
                          }}
                        />
                      </div>

                      {parentItem && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "10px 14px",
                            borderRadius: 10,
                            background: "rgba(255, 255, 255, 0.75)",
                            border: `1.5px solid ${group.borderColor}`,
                            boxShadow: "0 2px 6px rgba(0,0,0,0.02)",
                            marginBottom: childItems.length > 0 ? 10 : 0,
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
                            <SafetyCertificateOutlined
                              style={{ color: group.color, fontSize: 14 }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <Text
                                strong
                                style={{
                                  fontSize: 13,
                                  color: group.color,
                                  display: "block",
                                }}
                              >
                                {formatPermissionLabel(parentItem.per_name)}
                              </Text>
                              <Text
                                style={{
                                  fontSize: 10,
                                  color: "var(--fluid-text-muted)",
                                  fontFamily: "monospace",
                                }}
                              >
                                {parentItem.per_name}
                              </Text>
                            </div>
                          </div>
                          <Tooltip
                            title={parentItem.per_status === 1 || parentItem.per_status === "1" ? "Disable Module Access" : "Enable Module Access"}
                          >
                            <Switch
                              size="default"
                              checked={parentItem.per_status === 1 || parentItem.per_status === "1"}
                              onChange={(checked) =>
                                handlePermissionToggle(parentItem, checked)
                              }
                              style={{
                                backgroundColor: parentItem.per_status === 1 || parentItem.per_status === "1" ? group.color : undefined,
                                flexShrink: 0,
                              }}
                            />
                          </Tooltip>
                        </div>
                      )}

                      {childItems.length > 0 && (
                        <div >
                          <Button
                            type="text"
                            size="small"
                            onClick={() => toggleGroupExpand(group.key)}
                            icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
                            style={{
                              color: group.color,
                              width: "100%",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: "6px 10px",
                              background: "rgba(255,255,255,0.45)",
                              borderRadius: 8,
                              fontSize: "12px",
                              fontWeight: 600,
                              border: "1px solid rgba(255,255,255,0.7)"
                            }}
                          >
                            <span>Sub-permissions ({childItems.length})</span>
                            <span>{isExpanded ? "Collapse" : "Expand"}</span>
                          </Button>

                          {isExpanded && (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 6,
                                marginTop: 8,
                                paddingLeft: 12,
                                borderLeft: `2px dashed ${group.borderColor}`,
                                paddingBottom: 4
                              }}
                            >
                              {getChildSections(group, childItems).map((section) => (
                                <div key={section.label} style={{ marginBottom: 2 }}>
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 6,
                                      margin: "10px 0 4px",
                                    }}
                                  >
                                    <span
                                      style={{
                                        width: 14,
                                        height: 3,
                                        borderRadius: 2,
                                        background: group.color,
                                        flexShrink: 0,
                                      }}
                                    />
                                    <Text
                                      strong
                                      style={{
                                        fontSize: 10,
                                        textTransform: "uppercase",
                                        letterSpacing: 0.6,
                                        color: group.color,
                                      }}
                                    >
                                      {section.label}
                                    </Text>
                                  </div>
                                  {section.items.map((perm) => {
                                    const isEnabled = perm.per_status === 1 || perm.per_status === "1";
                                    return (
                                      <div
                                        key={perm.roleper_id}
                                        style={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          padding: "8px 12px",
                                          borderRadius: 8,
                                          background: "rgba(255, 255, 255, 0.45)",
                                          border: "1px solid rgba(255,255,255,0.7)",
                                          boxShadow: "0 1px 3px rgba(0,0,0,0.01)",
                                          transition: "all 0.2s",
                                        }}
                                      >
                                        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
                                          <div
                                            style={{
                                              width: 6,
                                              height: 6,
                                              borderRadius: "50%",
                                              background: isEnabled ? group.color : "#c0c0c0",
                                              flexShrink: 0,
                                            }}
                                          />
                                          <div style={{ flex: 1, minWidth: 0 }}>
                                            <Text
                                              style={{
                                                fontSize: 12,
                                                color: "var(--fluid-text)",
                                                display: "block",
                                              }}
                                            >
                                              {formatPermissionLabel(perm.per_name)}
                                            </Text>
                                            <Text
                                              style={{
                                                fontSize: 9,
                                                color: "var(--fluid-text-muted)",
                                                fontFamily: "monospace",
                                              }}
                                            >
                                              {perm.per_name}
                                            </Text>
                                          </div>
                                        </div>
                                        <Tooltip
                                          title={isEnabled ? "Disable permission" : "Enable permission"}
                                        >
                                          <Switch
                                            size="small"
                                            checked={isEnabled}
                                            onChange={(checked) =>
                                              handlePermissionToggle(perm, checked)
                                            }
                                            style={{
                                              backgroundColor: isEnabled ? group.color : undefined,
                                              flexShrink: 0,
                                            }}
                                          />
                                        </Tooltip>
                                      </div>
                                    );
                                  })}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </Col>
                );
              })}

              {ungrouped.length > 0 && (
                <Col xs={24}>
                  <div className="fluid-card">
                    <div className="fluid-card-header">
                      <h3 className="fluid-card-title">Other Permissions</h3>
                    </div>
                    {ungrouped.map((perm) => (
                      <div
                        key={perm.roleper_id}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "8px 0",
                          borderBottom: "1px solid var(--fluid-border)",
                        }}
                      >
                        <Text>{formatPermissionLabel(perm.per_name)}</Text>
                        <Switch
                          size="small"
                          checked={perm.per_status === 1 || perm.per_status === "1"}
                          onChange={(checked) => handlePermissionToggle(perm, checked)}
                        />
                      </div>
                    ))}
                  </div>
                </Col>
              )}

              {filteredPermissions.length === 0 && !loading && (
                <Col xs={24}>
                  <div
                    className="fluid-card"
                    style={{ textAlign: "center", padding: "40px 20px" }}
                  >
                    <LockOutlined
                      style={{ fontSize: 40, color: "var(--fluid-primary)", opacity: 0.4 }}
                    />
                    <p style={{ color: "var(--fluid-text-muted)", marginTop: 12 }}>
                      {searchText
                        ? "No permissions match your search."
                        : "No permissions found for this role."}
                    </p>
                    {searchText && (
                      <Button
                        size="small"
                        icon={<ClearOutlined />}
                        onClick={() => setSearchText("")}
                      >
                        Clear Search
                      </Button>
                    )}
                  </div>
                </Col>
              )}
            </Row>
          </>
        )}

        {/* Empty state - no role selected */}
        {!selectedRole && !loading && (
          <div
            className="fluid-card"
            style={{ textAlign: "center", padding: "60px 20px" }}
          >
            <SafetyCertificateOutlined
              style={{ fontSize: 56, color: "var(--fluid-primary)", opacity: 0.3 }}
            />
            <Title level={4} style={{ color: "var(--fluid-text-muted)", marginTop: 16 }}>
              Select a Role
            </Title>
            <Text type="secondary">
              Choose a role from the dropdown above to manage its permissions.
            </Text>
          </div>
        )}
      </Content>

      {/* Create / Edit Role Modal */}
      <Modal
        title={
          <Space>
            {modalMode === "create" ? <PlusOutlined /> : <EditOutlined />}
            {modalMode === "create" ? "Create New Role" : "Rename Role"}
          </Space>
        }
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        confirmLoading={submitting}
        okText={modalMode === "create" ? "Create Role" : "Save Changes"}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="role_name"
            label="Role Name"
            rules={[{ required: true, message: "Please enter a role name" }]}
          >
            <Input placeholder="e.g., Field Manager" size="large" />
          </Form.Item>
          {modalMode === "create" && (
            <div
              style={{
                background: "var(--fluid-bg-light)",
                border: "1px solid var(--fluid-border)",
                borderRadius: 8,
                padding: "10px 14px",
              }}
            >
              <Text type="secondary" style={{ fontSize: 12 }}>
                <CheckCircleOutlined style={{ marginRight: 6, color: "var(--fluid-primary)" }} />
                The new role will automatically inherit all system permission keys (disabled by default). You can enable them individually after creation.
              </Text>
            </div>
          )}
        </Form>
      </Modal>

      <Footerdiv />
    </Layout>
  );
};

export default ManageRoles;
